import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LeadMagnetPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [playbookAssets, setPlaybookAssets] = useState({ mockup: null, pdf: null });

  // Fetch playbook assets from library
  useEffect(() => {
    fetch(`${API}/api/assets?search=playbook`)
      .then(r => r.ok ? r.json() : [])
      .then(assets => {
        const mockup = assets.find(a => a.asset_type === 'image' && a.original_name?.toLowerCase().includes('mockup'));
        const pdf = assets.find(a => a.asset_type === 'pdf');
        setPlaybookAssets({
          mockup: mockup ? `${API}${mockup.file_url}` : `${API}/api/upload/images/producers-playbook-mockup.png`,
          pdf: pdf ? `${API}${pdf.file_url}` : null
        });
      }).catch(() => {});
  }, []);

  // Listen for manual trigger events from other components
  useEffect(() => {
    const handleTrigger = () => {
      setIsVisible(true);
    };
    window.addEventListener('trigger-lead-magnet', handleTrigger);
    return () => window.removeEventListener('trigger-lead-magnet', handleTrigger);
  }, []);

  // Check if popup should show
  const shouldShowPopup = useCallback(() => {
    // Never show if already subscribed
    if (localStorage.getItem('swp_subscribed') === 'true') return false;
    
    // Don't show if dismissed within last 14-30 days (use 21 as middle ground)
    const dismissedAt = localStorage.getItem('swp_popup_dismissed');
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 21) return false;
    }

    // Check if first-time visitor (for time trigger)
    const hasVisited = localStorage.getItem('swp_has_visited');

    // Only show on specific pages
    const allowedPaths = ['/', '/about', '/blog'];
    const currentPath = window.location.pathname;
    const isBlogPost = currentPath.startsWith('/blog/');
    
    if (!allowedPaths.includes(currentPath) && !isBlogPost) return false;

    return { allowed: true, isFirstVisit: !hasVisited, isBlogPost };
  }, []);

  useEffect(() => {
    const checkResult = shouldShowPopup();
    if (!checkResult || !checkResult.allowed) return;

    // Mark as visited
    localStorage.setItem('swp_has_visited', 'true');

    let triggered = false;
    let timeoutId;
    let scrollTriggered = false;

    // Timer trigger - 45 seconds for first-time visitors only
    if (checkResult.isFirstVisit) {
      timeoutId = setTimeout(() => {
        if (!triggered && shouldShowPopup()?.allowed) {
          triggered = true;
          setIsVisible(true);
        }
      }, 45000);
    }

    // Scroll trigger - 70% for blog posts, 50% for other pages
    const scrollThreshold = checkResult.isBlogPost ? 70 : 50;
    const handleScroll = () => {
      if (scrollTriggered || triggered) return;
      
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent >= scrollThreshold && shouldShowPopup()?.allowed) {
        scrollTriggered = true;
        triggered = true;
        setIsVisible(true);
      }
    };

    // Exit intent trigger (desktop only)
    const handleMouseLeave = (e) => {
      if (triggered) return;
      if (e.clientY <= 0 && shouldShowPopup()?.allowed) {
        triggered = true;
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [shouldShowPopup]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('swp_popup_dismissed', new Date().toISOString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          name,
          source: 'lead_magnet_popup',
          lead_magnet: 'producers_playbook'
        })
      });

      if (response.ok) {
        toast.success("You're in! Check your email for the Producer's Playbook.");
        localStorage.setItem('swp_subscribed', 'true');
        setIsVisible(false);
      } else {
        let errorMsg = 'Something went wrong';
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch {
          // Response body wasn't JSON
        }
        if (errorMsg.includes('already subscribed')) {
          toast.info("You're already in the pack! Check your email.");
          localStorage.setItem('swp_subscribed', 'true');
          setIsVisible(false);
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        toast.error('Unable to connect. Please check your internet and try again.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-swp-deep/90 backdrop-blur-sm"
            onClick={handleDismiss}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-md bg-[#0a0a0a] border border-swp-border rounded-2xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Close Button */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 p-2 text-swp-white-ghost/70 hover:text-swp-white transition-colors rounded-swp hover:bg-white/5 z-10"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            {/* Content */}
            <div className="p-8">
              {/* Mockup Image */}
              <div className="mb-6">
                <img 
                  src={playbookAssets.mockup}
                  alt="The Producer's Playbook"
                  className="w-48 h-auto mx-auto"
                />
              </div>

              {/* Heading */}
              <h2 
                className="text-2xl font-bold text-white text-center mb-2 font-display"
              >
                Get The Producer's Playbook
              </h2>
              <p className="text-swp-ice text-sm text-center mb-4 font-mono uppercase tracking-widest">
                Free
              </p>

              {/* Description */}
              <p className="text-swp-white-ghost text-center text-sm mb-6 leading-relaxed">
                Practical tools we actually use. No fluff.
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-5 py-4 rounded-swp bg-swp-black border border-swp-border text-white placeholder-gray-500 focus:outline-none focus:border-swp-ice transition-colors"
                  disabled={submitting}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-5 py-4 rounded-swp bg-swp-black border border-swp-border text-white placeholder-gray-500 focus:outline-none focus:border-swp-ice transition-colors"
                  disabled={submitting}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-4 bg-swp-ice hover:bg-swp-ice disabled:bg-swp-ice/50 text-white rounded-swp font-mono text-sm uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>Get the Playbook</>
                  )}
                </button>
              </form>

              {/* Footer - Newsletter mention */}
              <p className="text-swp-white-ghost/50 text-xs text-center mt-4">
                You'll also join our newsletter for industry updates. Unsubscribe anytime.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LeadMagnetPopup;
